import { Link } from 'react-router-dom';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      {/* Header */}
      <header className="py-6 px-6 border-b border-gray-800">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center cursor-pointer">
            <img src="/logo.png" alt="Kuze Logo" className="h-10 w-auto" />
          </Link>
          <Link to="/" className="text-gray-400 hover:text-white transition-colors flex items-center gap-2 cursor-pointer">
            <i className="ri-arrow-left-line"></i>
            Back to Home
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-normal">Privacy Policy</h1>
          <p className="text-gray-400 mb-12">Last updated: January 1, 2026</p>

          <div className="space-y-10 text-gray-300">
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4 leading-normal">1. Introduction</h2>
              <p className="leading-relaxed mb-4">
                Kuze ("we", "our", "us") is a real-time multiplayer party game application operated by Think Heart Co. AB.
              </p>
              <p className="leading-relaxed mb-4">
                We respect your privacy and are committed to protecting your data. This Privacy Policy explains what information we collect, how we use it, and your rights.
              </p>
              <p className="leading-relaxed">
                By using Kuze, you agree to this Privacy Policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4 leading-normal">2. Information We Collect</h2>
              <p className="leading-relaxed mb-6">
                We collect only the minimum information necessary to operate, improve, and grow the app.
              </p>
              <h3 className="text-lg font-medium text-white mb-3 leading-normal">2.1 Temporary Display Information</h3>
              <p className="leading-relaxed mb-4">
                When joining a game session, you may choose:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-4 ml-4">
                <li>A display name (nickname)</li>
                <li>An avatar</li>
              </ul>
              <p className="leading-relaxed mb-6">
                This information:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-6 ml-4">
                <li>Is not linked to a persistent account</li>
                <li>Is not verified against your real identity</li>
                <li>Exists only within the active game session</li>
                <li>Is deleted after the session ends</li>
              </ul>
              
              <h3 className="text-lg font-medium text-white mb-3 leading-normal">2.2 Game Session Data</h3>
              <p className="leading-relaxed mb-4">
                During gameplay, we process:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-4 ml-4">
                <li>Votes</li>
                <li>Answers</li>
                <li>Selections</li>
                <li>Game-related interactions</li>
              </ul>
              <p className="leading-relaxed mb-4">
                This data is used solely to operate the live multiplayer session.
              </p>
              <p className="leading-relaxed mb-4">
                Session data:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-4 ml-4">
                <li>Is temporary</li>
                <li>Is not stored long-term</li>
                <li>Is automatically deleted after session completion or a short cleanup period</li>
              </ul>
              <p className="leading-relaxed mb-6">
                We do not create user profiles or track individual gameplay history across sessions.
              </p>

              <h3 className="text-lg font-medium text-white mb-3 leading-normal">2.3 Analytics Data</h3>
              <p className="leading-relaxed mb-4">
                We use Google Firebase Analytics to collect anonymous and aggregated usage data, including:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-4 ml-4">
                <li>Games played</li>
                <li>Feature usage</li>
                <li>Session duration</li>
                <li>App version</li>
                <li>Device type</li>
                <li>Country-level geographic region</li>
              </ul>
              <p className="leading-relaxed mb-4">
                This data:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-4 ml-4">
                <li>Does not include names, emails, or contact details</li>
                <li>Is not used to identify individual users</li>
                <li>Is used only to improve stability, performance, and game design</li>
              </ul>
              <p className="leading-relaxed mb-6">
                Firebase may automatically collect technical data such as device identifiers and IP addresses as part of its standard functionality. This is processed according to Google's privacy policy.
              </p>

              <h3 className="text-lg font-medium text-white mb-3 leading-normal">2.4 In-App Purchases</h3>
              <p className="leading-relaxed mb-4">
                Kuze offers optional in-app purchases such as premium game packs or features.
              </p>
              <p className="leading-relaxed mb-4">
                All payments are processed by:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-4 ml-4">
                <li>Apple App Store (Apple Inc.)</li>
                <li>Google Play Store (Google LLC)</li>
              </ul>
              <p className="leading-relaxed mb-4">
                We do not:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-4 ml-4">
                <li>Collect or store credit card information</li>
                <li>Process payments directly</li>
                <li>Receive full payment details</li>
              </ul>
              <p className="leading-relaxed mb-4">
                We may receive limited transaction information, such as:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-4 ml-4">
                <li>Confirmation that a purchase was completed</li>
                <li>The product purchased</li>
                <li>A transaction identifier</li>
              </ul>
              <p className="leading-relaxed mb-4">
                This information is used only to unlock purchased content and prevent fraud.
              </p>
              <p className="leading-relaxed mb-6">
                Payment processing is governed by Apple's or Google's privacy policies.
              </p>

              <h3 className="text-lg font-medium text-white mb-3 leading-normal">2.5 Social Sharing and Referral Tracking</h3>
              <p className="leading-relaxed mb-4">
                Kuze allows users to:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-4 ml-4">
                <li>Share invite links</li>
                <li>Share game links</li>
                <li>Share results, wins, or scoreboards</li>
                <li>Share content to social media platforms</li>
              </ul>
              <p className="leading-relaxed mb-4">
                When a user shares content, we may generate a unique or session-based referral link. We may track:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-4 ml-4">
                <li>That a link was created</li>
                <li>The type of content shared</li>
                <li>Whether the link was opened</li>
                <li>Whether it resulted in an app install or session start</li>
              </ul>
              <p className="leading-relaxed mb-4">
                This tracking is used solely to measure feature effectiveness and app growth.
              </p>
              <p className="leading-relaxed mb-4">
                We do not:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-4 ml-4">
                <li>Access your contacts</li>
                <li>Access your social media accounts</li>
                <li>Track who specifically received the link</li>
                <li>Identify individual recipients</li>
                <li>Build personal profiles of recipients</li>
              </ul>
              <p className="leading-relaxed mb-4">
                If a recipient installs the app after using a referral link, we may record that the install originated from a referral, but we do not associate it with real-world identity.
              </p>
              <p className="leading-relaxed">
                Sharing via third-party platforms is governed by those platforms' own privacy policies.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4 leading-normal">3. What We Do Not Collect</h2>
              <p className="leading-relaxed mb-4">
                We do not:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-4 ml-4">
                <li>Require account creation</li>
                <li>Collect email addresses or phone numbers</li>
                <li>Collect real names</li>
                <li>Collect precise location data</li>
                <li>Access contacts or photos</li>
                <li>Store payment card information</li>
                <li>Sell personal data</li>
                <li>Share personal data for advertising</li>
              </ul>
              <p className="leading-relaxed">
                Kuze does not use third-party advertising networks.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4 leading-normal">4. Data Storage and Security</h2>
              <p className="leading-relaxed mb-4">
                Game session data is stored temporarily using Google Firebase (Firestore) and automatically deleted after sessions end.
              </p>
              <p className="leading-relaxed mb-4">
                Analytics and referral tracking data are processed in aggregated or pseudonymous form.
              </p>
              <p className="leading-relaxed">
                We rely on industry-standard security measures provided by Google Cloud infrastructure to protect data from unauthorized access.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4 leading-normal">5. Third-Party Services</h2>
              <p className="leading-relaxed mb-4">
                We use the following third-party services:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-6 ml-4">
                <li>Google Firebase (Firestore, Hosting, Analytics)</li>
                <li>Apple App Store (for iOS purchases)</li>
                <li>Google Play Store (for Android purchases)</li>
              </ul>
              <p className="leading-relaxed mb-4">
                These services may process data according to their own privacy policies:
              </p>
              <div className="bg-gray-800/50 rounded-xl p-6 space-y-2">
                <p><strong className="text-white">Google:</strong> <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[#14B8A6] hover:underline cursor-pointer">https://policies.google.com/privacy</a></p>
                <p><strong className="text-white">Apple:</strong> <a href="https://www.apple.com/legal/privacy/" target="_blank" rel="noopener noreferrer" className="text-[#14B8A6] hover:underline cursor-pointer">https://www.apple.com/legal/privacy/</a></p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4 leading-normal">6. Legal Basis for Processing (EEA/UK Users)</h2>
              <p className="leading-relaxed mb-4">
                If you are located in the European Economic Area or United Kingdom, we process data based on:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Legitimate interest (to operate and improve Kuze)</li>
                <li>Contractual necessity (to deliver purchased content)</li>
                <li>Consent, where required under applicable law</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4 leading-normal">7. Data Retention</h2>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong className="text-white">Temporary session data:</strong> Deleted after session completion.</li>
                <li><strong className="text-white">Referral tracking data:</strong> Retained only as long as necessary for analytics and growth measurement.</li>
                <li><strong className="text-white">Purchase validation data:</strong> Retained as long as required to provide purchased content and prevent fraud.</li>
                <li><strong className="text-white">Analytics data:</strong> Retained in aggregated form according to Firebase retention settings.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4 leading-normal">8. Children's Privacy</h2>
              <p className="leading-relaxed mb-4">
                Kuze is intended for users aged 18 and older.
              </p>
              <p className="leading-relaxed">
                We do not knowingly collect personal data from individuals under 18. If you believe a minor has provided data, please contact us and we will take appropriate steps to remove it.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4 leading-normal">9. Your Rights</h2>
              <p className="leading-relaxed mb-4">
                Depending on your location, you may have the right to:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-4 ml-4">
                <li>Request access to your data</li>
                <li>Request deletion</li>
                <li>Object to certain processing</li>
                <li>Withdraw consent where applicable</li>
              </ul>
              <p className="leading-relaxed">
                Because Kuze does not maintain user accounts or identifiable personal records, we may not be able to associate analytics or referral data with a specific individual.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4 leading-normal">10. Changes to This Policy</h2>
              <p className="leading-relaxed">
                We may update this Privacy Policy from time to time. Updates will be reflected by revising the "Last updated" date.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4 leading-normal">11. Contact Us</h2>
              <p className="leading-relaxed mb-4">
                If you have any questions about this Privacy Policy or our privacy practices, please contact us at:
              </p>
              <div className="bg-gray-800/50 rounded-xl p-6">
                <p className="mb-2"><strong className="text-white">Company:</strong> Think Heart Apps AB</p>
                <p className="mb-2"><strong className="text-white">Email:</strong> hello@thinkheart.co</p>
                <p><strong className="text-white">Website:</strong> https://kuze.app</p>
              </div>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-gray-800">
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-gray-500 text-sm">© 2026 Kuze. Built for real-life parties.</p>
          <div className="flex items-center gap-6">
            <Link to="/privacy" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">Privacy</Link>
            <Link to="/terms" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
